import { type FieldConfig, parseFieldConfig, serialiseFieldConfig } from './elements/fieldConfig';
import { elementFor } from './elements/registry';
import type {
  QuestionnaireDetailDto,
  SaveQuestionnaireInput,
} from './schemas/questionnaireSchemas';

// Editable builder model. `key` is a stable client id for React; `serverId` is
// the persisted id (null for not-yet-saved rows → the backend creates them).
export interface BuilderField {
  key: string;
  serverId: string | null;
  fieldType: string;
  label: string;
  placeholder: string;
  validationMessage: string;
  /**
   * Type-specific configuration, held PARSED in the builder and serialised only
   * at the save boundary. Keeping it as an object means the config panel edits
   * `config.options` directly instead of re-parsing a JSON string on every
   * keystroke.
   */
  config: FieldConfig;
  isMandatory: boolean;
}

export interface BuilderSection {
  key: string;
  serverId: string | null;
  /** Builder tab label ("Section 1"). Internal. */
  name: string;
  /** Applicant-facing heading; falls back to `name` in the renderer when blank. */
  title: string;
  /** Applicant-facing sub-heading. */
  description: string;
  fields: BuilderField[];
}

export interface BuilderModel {
  name: string;
  description: string;
  sections: BuilderSection[];
}

function newKey(): string {
  return crypto.randomUUID();
}

/**
 * A brand-new field of `fieldType`, seeded from the element registry.
 *
 * The defaults table that used to live here is gone: it named nine types that no longer exist
 * and would have had to be kept in step with the palette by hand. The registry is the one place
 * an element type is described.
 *
 * `config` is deep-copied — the registry's defaults are shared module-level objects, and handing
 * a field a reference to them would let editing one Address field's sub-fields silently rewrite
 * the default for every Address field added afterwards.
 */
export function newField(fieldType: string): BuilderField {
  const { defaults } = elementFor(fieldType);
  return {
    key: newKey(),
    serverId: null,
    fieldType,
    label: defaults.label,
    placeholder: defaults.placeholder,
    validationMessage: defaults.validationMessage,
    config: structuredClone(defaults.config),
    // Mandatory is always off for a new field. The reference switches it on for
    // multiple-choice / date-range / number only, which is mock noise, not a rule — an admin
    // should decide what an applicant is forced to answer, not inherit it from the element type.
    isMandatory: false,
  };
}

export function newSection(index: number): BuilderSection {
  return {
    key: newKey(),
    serverId: null,
    name: `Section ${index}`,
    title: '',
    description: '',
    fields: [],
  };
}

export function emptyModel(): BuilderModel {
  return {
    name: 'New Questionnaire',
    description: 'Set up a questionnaire to capture user data as part of verification.',
    sections: [newSection(1)],
  };
}

export function fromDetail(d: QuestionnaireDetailDto): BuilderModel {
  const sections: BuilderSection[] = d.sections.map((s) => ({
    key: newKey(),
    serverId: s.id,
    name: s.name,
    title: s.title ?? '',
    description: s.description ?? '',
    fields: s.fields.map((f) => ({
      key: newKey(),
      serverId: f.id,
      fieldType: f.fieldType,
      label: f.label,
      placeholder: f.placeholder ?? '',
      validationMessage: f.validationMessage ?? '',
      config: parseFieldConfig(f.configJson),
      isMandatory: f.isMandatory,
    })),
  }));

  return {
    name: d.name,
    description: d.description ?? '',
    sections: sections.length > 0 ? sections : [newSection(1)],
  };
}

/**
 * Copies the server ids from a just-saved response back onto the live model, BY POSITION.
 *
 * WHY THIS EXISTS AT ALL, and why it is not just `fromDetail(response)`. Autosave means a save can
 * land while the admin is mid-keystroke. Replacing the model with the response would throw away
 * every edit made during the request and move the caret; but ignoring the response entirely is
 * worse — new sections and fields have `serverId: null`, the Save reconciliation reads null as
 * "create me", and the NEXT autosave would create them all over again while deleting the rows the
 * previous one made. An autosaving builder that never learns its ids duplicates the whole
 * questionnaire on every keystroke.
 *
 * Position is a safe key because the response is the result of a payload this same model generated,
 * in this same order, moments earlier. If the shapes disagree — a section added mid-flight — the
 * extra rows simply keep `serverId: null` and are created by the next save.
 *
 * Nothing editable is touched: only `serverId` is written.
 */
export function mergeServerIds(model: BuilderModel, saved: QuestionnaireDetailDto): BuilderModel {
  return {
    ...model,
    sections: model.sections.map((section, sectionIndex) => {
      const savedSection = saved.sections[sectionIndex];
      if (!savedSection) return section;

      return {
        ...section,
        serverId: savedSection.id,
        fields: section.fields.map((field, fieldIndex) => {
          const savedField = savedSection.fields[fieldIndex];
          return savedField ? { ...field, serverId: savedField.id } : field;
        }),
      };
    }),
  };
}

export function toSaveInput(m: BuilderModel): SaveQuestionnaireInput {
  return {
    name: m.name.trim() || 'Untitled Questionnaire',
    description: m.description.trim() || null,
    sections: m.sections.map((s, si) => ({
      id: s.serverId,
      name: s.name.trim() || `Section ${si + 1}`,
      title: s.title.trim() || null,
      description: s.description.trim() || null,
      sortOrder: si,
      fields: s.fields.map((f, fi) => ({
        id: f.serverId,
        fieldType: f.fieldType,
        label: f.label.trim() || f.fieldType,
        placeholder: f.placeholder.trim() || null,
        validationMessage: f.validationMessage.trim() || null,
        configJson: serialiseFieldConfig(f.config),
        isMandatory: f.isMandatory,
        sortOrder: fi,
      })),
    })),
  };
}
